import { CompanyTypeSlug } from 'models/companies'

/**
 * Pure decision helpers for the "Push to Campaign" / "Unassign from Campaign"
 * bulk action that every Data Expansion table (DECommonTable) shares.
 *
 * Extracted so the parity rule is explicit and unit-testable: every table
 * (restaurants, universities/C&U, K-12, hospitals, nursing homes, and doors)
 * surfaces the campaign action identically. This guards against a regression
 * like FIR-6635, where the Colleges & Universities ("education-cu") table was
 * reported as missing the campaign option that other tables have.
 *
 * Visibility depends only on the global `enableCampaigns` feature flag and the
 * per-table `campaigns` feature opt-out. It is intentionally NOT gated on the
 * company type, so adding a new table gets campaign assignment for free. As of
 * FIR-3963 doors tables are included too: the door id routes to the door-scoped
 * assign endpoint, which the backend resolves to the parent chain proxy.
 */

export interface CampaignTableActionGate {
  /** Global `enable_campaigns` feature flag. */
  enableCampaigns: boolean
  /**
   * Whether this table renders doors rather than companies. No longer gates
   * visibility (doors are supported as of FIR-3963); kept so the caller can
   * pass the table shape and for routing decisions elsewhere.
   */
  isDoorsTable?: boolean
  /**
   * Per-table opt-out: `tableFeatures.campaigns` resolved via the table's
   * `getFeatureEnabled('campaigns')` (defaults to enabled when unset).
   */
  campaignsFeatureEnabled: boolean
  /** The company type the table renders (for documentation / future gating). */
  companyTypeSlug?: CompanyTypeSlug
}

/**
 * Whether the campaign bulk action should be rendered for this table at all.
 * True for any table (companies or doors) when campaigns are enabled globally
 * and not opted out for the specific table.
 */
export function shouldShowCampaignAction(
  gate: CampaignTableActionGate
): boolean {
  return Boolean(gate.enableCampaigns && gate.campaignsFeatureEnabled)
}

export type CampaignActionKind = 'push' | 'unassign'

/**
 * Which campaign action to show: "unassign" when the table is already scoped to
 * a campaign (campaign targets view), otherwise "push" (assign to a campaign).
 */
export function getCampaignActionKind(
  hasCampaign: boolean
): CampaignActionKind {
  return hasCampaign ? 'unassign' : 'push'
}
